import { useDispatch, useSelector } from "react-redux";
import { CustomizationProvider, CustomizationProviderProps } from "@twilio-paste/core/customization";
import { CSSProperties, FC, useEffect } from "react";

import { RootContainer } from "./RootContainer";
import { AppState, EngagementPhase } from "../store/definitions";
import { sessionDataHandler } from "../sessionDataHandler";
import { initSession } from "../store/actions/initActions";
import { changeEngagementPhase, setSessionPreEngagementData } from "../store/actions/genericActions";

const AnyCustomizationProvider: FC<CustomizationProviderProps & { style: CSSProperties }> = CustomizationProvider;

export function WebchatWidget() {
    const theme = useSelector((state: AppState) => state.config.theme);
    const context = useSelector((state: AppState) => state.config.context);
    const resumeSessionConfig = useSelector((state: AppState) => state.config.resumeSessionConfig);
    const dispatch = useDispatch();

    useEffect(() => {
        let isCancelled = false;

        const initializeWidgetSession = async () => {
            if (resumeSessionConfig?.showPreEngagementForm) {
                dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
                return;
            }

            const existingSession = sessionDataHandler.tryResumeExistingSession();
            if (existingSession) {
                if (!isCancelled) {
                    dispatch(initSession(existingSession, { mode: "storedRestore" }));
                }
                return;
            }

            const webchatSessionResponse = await sessionDataHandler.fetchWebchatSession({
                resumeSessionConfig,
                contextData: context
            });
            if (webchatSessionResponse?.sessionData) {
                if (!isCancelled) {
                    dispatch(initSession(webchatSessionResponse.sessionData, { mode: "conversationResume" }));
                }
                return;
            }

            if (!isCancelled) {
                if (webchatSessionResponse?.sessionMode === "new" && webchatSessionResponse.sessionPreEngagementData) {
                    dispatch(setSessionPreEngagementData(webchatSessionResponse.sessionPreEngagementData));
                }
                dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
            }
        };

        initializeWidgetSession();

        return () => {
            isCancelled = true;
        };
    }, [context, dispatch, resumeSessionConfig]);

    return (
        <AnyCustomizationProvider
            baseTheme={theme?.isLight ? "default" : "dark"}
            theme={theme?.overrides}
            elements={{
                ALERT: {
                    paddingTop: "space30",
                    paddingBottom: "space30"
                },
                BUTTON: {
                    "&[aria-disabled='true'][color='colorTextLink']": {
                        color: "colorTextLinkWeak"
                    }
                },
                ...theme?.elements
            }}
            style={{ minHeight: "100%", minWidth: "100%" }}
        >
            <RootContainer />
        </AnyCustomizationProvider>
    );
}
